import React, { createContext, useContext, useEffect, useState } from "react";
import { doc, collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { useAuth } from "./AuthContext";
import { partnerId } from "./partnerConfig";

interface KioskData {
    id: string;
    docId: string;
    status: "PENDING" | "ACTIVE" | "INACTIVE" | "FULL";
    name: string;
    address: string;
    lat: number;
    lng: number;
    maxCapacity: number;
    currentCapacity: number;
    message: string;
    photo: string;
    timestamp: number;
}

interface KioskContextType {
    kiosks: KioskData[];
    favorites: string[];
    loading: { [key: string]: boolean };
}

const KioskContext = createContext<KioskContextType | null>(null);

export const KioskProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { user } = useAuth();

    const [kiosks, setKiosks] = useState<KioskData[]>([]);
    const [favorites, setFavorites] = useState<string[]>([]);

    const [loading, setLoading] = useState<{ [key: string]: boolean }>({
        kiosks: true,
        favorites: true,
    });

    useEffect(() => {
        if (!user) return;

        const unsubscribeKiosks = onSnapshot(
            query(
                collection(db, "kiosk"),
                where("status", "in", ["ACTIVE", "FULL"]),
                where("partnerId", "==", partnerId)
            ),
            (snapshot) => {
                const kioskLists = snapshot.docs.map((doc) => doc.data()).sort((a, b) => a.timestamp - b.timestamp);
                setKiosks(kioskLists as KioskData[]);
                setLoading((prev) => ({ ...prev, kiosks: false }));
            }
        );

        const unsubscribeFavorites = onSnapshot(
            query(collection(db, "member"), where("uid", "==", user!.uid)),
            (snapshot) => {
                const memberData = snapshot.docs.map((doc) => doc.data())[0];
                setFavorites(memberData!.savedKiosk);
                setLoading((prev) => ({ ...prev, favorites: false }));
            }
        );

        return () => {
            unsubscribeKiosks();
            unsubscribeFavorites();
        };
    }, [user]);

    return (
        <KioskContext.Provider
            value={{
                kiosks,
                favorites,
                loading,
            }}
        >
            {children}
        </KioskContext.Provider>
    );
};

export const useKiosk = (): KioskContextType => {
    const context = useContext(KioskContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
